// Simple racing-line autopilot for headless simulations: pure-pursuit steering
// and a curvature speed profile (grip-scaled at runtime).
import type { Car, CarInput } from '../../src/sim/car';
import { carParams } from '../../src/sim/car';
import { aeroParams, AIR_DENSITY } from '../../src/sim/aero';

export class Autopilot {
  private readonly vMax: number[] = [];
  private idx = 0;
  /** Target speed from the last control() call, m/s. */
  lastTarget = 0;

  constructor(
    private readonly line: ReadonlyArray<{ x: number; z: number }>,
    private readonly factor = 0.93,
    margin = 0.92,
    /** Share of the grip-limited deceleration the braking points assume. */
    brakeFactor = 0.85,
  ) {
    const n = line.length;
    const m = carParams.mass.value;
    const mu = carParams.tyreMu.value * margin;
    const clA = aeroParams.clACorner.value;
    for (let i = 0; i < n; i++) {
      const a = line[(i - 3 + n) % n]!, b = line[i]!, c = line[(i + 3) % n]!;
      const area = (b.x - a.x) * (c.z - a.z) - (b.z - a.z) * (c.x - a.x);
      const ab = Math.hypot(b.x - a.x, b.z - a.z), bc = Math.hypot(c.x - b.x, c.z - b.z), ca = Math.hypot(a.x - c.x, a.z - c.z);
      const k = Math.abs(2 * area) / (ab * bc * ca || 1);
      const R = k > 1e-6 ? 1 / k : 1e6;
      const denom = 1 - (mu * AIR_DENSITY * clA * R) / (2 * m);
      this.vMax.push(denom > 0 ? Math.min(95, Math.sqrt((mu * 9.81 * R) / denom)) : 95);
    }
    const ds = (i: number): number => Math.hypot(line[(i + 1) % n]!.x - line[i]!.x, line[(i + 1) % n]!.z - line[i]!.z);
    for (let pass = 0; pass < 2; pass++)
      for (let i = n - 1; i >= 0; i--) {
        const next = this.vMax[(i + 1) % n]!;
        const v = this.vMax[i]!;
        const decel = 9.81 * mu * (1 + (AIR_DENSITY * clA * v * v) / (2 * m * 9.81)) * brakeFactor;
        this.vMax[i] = Math.min(v, Math.sqrt(next * next + 2 * decel * ds(i)));
      }
  }

  /** Snap the line index to the nearest point (after a teleport, e.g. pit exit). */
  relocate(x: number, z: number): void {
    let best = 0, bd = Infinity;
    this.line.forEach((p, j) => {
      const d = (p.x - x) ** 2 + (p.z - z) ** 2;
      if (d < bd) { bd = d; best = j; }
    });
    this.idx = best;
  }

  control(car: Car): CarInput {
    const n = this.line.length;
    let best = this.idx, bd = Infinity;
    for (let k = -20; k <= 60; k++) {
      const j = (this.idx + k + n) % n, p = this.line[j]!;
      const d = (p.x - car.x) ** 2 + (p.z - car.z) ** 2;
      if (d < bd) { bd = d; best = j; }
    }
    this.idx = best;
    const look = Math.round(Math.max(8, car.speed * 0.45) / 5);
    const target = this.line[(best + look) % n]!;
    let err = Math.atan2(target.x - car.x, target.z - car.z) - car.heading;
    err = Math.atan2(Math.sin(err), Math.cos(err));
    const steer = Math.max(-1, Math.min(1, (err * 2.2) / (car.maxWheelAngle() * 2)));
    // Corner speeds scale with the square root of the available grip.
    const grip = Math.min(car.mods.gripFront, car.mods.gripRear);
    const vTarget = this.vMax[(best + 2) % n]! * this.factor * Math.sqrt(grip);
    this.lastTarget = vTarget;
    const throttle = car.speed < vTarget ? 1 : 0;
    const brake = car.speed > vTarget + 1.5 ? Math.min(1, (car.speed - vTarget) / 4) : 0;
    return { steer, throttle, brake };
  }
}
